import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatTone = 'teal' | 'sky' | 'emerald' | 'amber' | 'violet' | 'rose';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon?: LucideIcon;
  tone?: StatTone;
  className?: string;
}

const tones: Record<StatTone, { iconBg: string; iconText: string }> = {
  teal:    { iconBg: 'bg-teal-50 dark:bg-teal-900/20',       iconText: 'text-teal-700 dark:text-teal-400' },
  sky:     { iconBg: 'bg-sky-50 dark:bg-sky-900/20',         iconText: 'text-sky-700 dark:text-sky-400' },
  emerald: { iconBg: 'bg-emerald-50 dark:bg-emerald-900/20', iconText: 'text-emerald-700 dark:text-emerald-400' },
  amber:   { iconBg: 'bg-amber-50 dark:bg-amber-900/20',     iconText: 'text-amber-700 dark:text-amber-400' },
  violet:  { iconBg: 'bg-violet-50 dark:bg-violet-900/20',   iconText: 'text-violet-700 dark:text-violet-400' },
  rose:    { iconBg: 'bg-rose-50 dark:bg-rose-900/20',       iconText: 'text-rose-700 dark:text-rose-400' },
};

export function StatCard({ label, value, hint, icon: Icon, tone = 'teal', className }: StatCardProps) {
  const t = tones[tone];
  return (
    <div
      className={cn(
        'group rounded-xl bg-card p-3 sm:p-5 shadow-card transition-shadow hover:shadow-card-hover',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className="mt-1.5 text-2xl sm:text-3xl font-semibold leading-none tracking-tight text-foreground">
            {value}
          </p>
          {hint && (
            <p className="mt-2.5 text-xs sm:text-sm leading-snug text-muted-foreground line-clamp-2">
              {hint}
            </p>
          )}
        </div>
        {Icon && (
          <div
            className={cn(
              'flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105',
              t.iconBg,
            )}
          >
            <Icon className={cn('h-[18px] w-[18px]', t.iconText)} />
          </div>
        )}
      </div>
    </div>
  );
}
